import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import AppShell from '../shell/AppShell';
import { UserContext } from '../../UserContext';
import dataService from '../../services/dataService';
import '../../features/brand/cwm-tokens.css';

/**
 * /unified/server-dashboard — fast allergy lookup for waitstaff, rebuilt in
 * brand primitives. Pick a restaurant → pick a menu → search + tap allergens
 * to hide matching items → scan brand-styled menu cards with allergen badges
 * and prep-method tags. Guest-safety defaults: "hide items containing" rather
 * than "show only", so servers can't accidentally surface something unsafe.
 */

const ALLERGEN_TONES = {
  Gluten:    { bg: 'var(--cwm-amber-light)', fg: 'var(--cwm-amber-text)', dot: 'var(--cwm-amber)' },
  Dairy:     { bg: 'var(--cwm-blue-pale)',   fg: 'var(--cwm-blue-deep)',  dot: 'var(--cwm-blue)' },
  Nuts:      { bg: 'var(--cwm-red-light)',   fg: 'var(--cwm-red-text)',   dot: 'var(--cwm-skip)' },
  Peanut:    { bg: 'var(--cwm-red-light)',   fg: 'var(--cwm-red-text)',   dot: 'var(--cwm-skip)' },
  Shellfish: { bg: '#F1E8FE',                fg: '#5B21B6',               dot: '#7C3AED' },
  Eggs:      { bg: 'var(--cwm-amber-light)', fg: 'var(--cwm-amber-text)', dot: 'var(--cwm-amber)' },
  Soy:       { bg: 'var(--cwm-green-light)', fg: 'var(--cwm-green-text)', dot: 'var(--cwm-green)' },
  Fish:      { bg: 'var(--cwm-blue-pale)',   fg: 'var(--cwm-blue-deep)',  dot: 'var(--cwm-blue)' },
  Sesame:    { bg: '#FFE9F1',                fg: '#9D174D',               dot: '#DB2777' },
};

const COMMON_ALLERGENS = ['Gluten', 'Dairy', 'Nuts', 'Shellfish', 'Eggs', 'Soy'];

const SERVER_VIEWS = [
  { id: 'menu',     label: 'Menu',     icon: 'menu' },
  { id: 'specials', label: 'Specials', icon: 'flame' },
  { id: 'upsells',  label: 'Upsells',  icon: 'arrow-up' },
  { id: 'quiz',     label: 'Quiz',     icon: 'quiz' },
];

const ALLERGEN_KEYWORDS = {
  Gluten: ['bread', 'flour', 'wheat', 'pasta', 'noodle', 'tortilla', 'bun', 'breaded', 'panko', 'soy sauce', 'tempura', 'croissant', 'pita', 'crust', 'toast'],
  Dairy: ['cheese', 'cream', 'milk', 'butter', 'yogurt', 'parmesan', 'mozzarella', 'cheddar', 'feta', 'ricotta', 'burrata', 'ranch', 'alfredo', 'crema'],
  Nuts: ['nut', 'almond', 'walnut', 'pecan', 'cashew', 'pistachio', 'hazelnut', 'peanut', 'macadamia', 'pesto'],
  Eggs: ['egg', 'mayo', 'mayonnaise', 'aioli', 'custard', 'mousse', 'quiche', 'carbonara'],
  Shellfish: ['shrimp', 'crab', 'lobster', 'prawn', 'scallop', 'clam', 'mussel', 'oyster', 'calamari', 'squid'],
  Fish: ['fish', 'salmon', 'tuna', 'cod', 'halibut', 'tilapia', 'trout', 'anchovy'],
  Soy: ['soy', 'tofu', 'edamame', 'miso', 'tempeh', 'teriyaki'],
  Sesame: ['sesame', 'tahini', 'hummus'],
};

export default function UnifiedServerDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId, isInitialized } = useContext(UserContext);
  const initialRestaurantId = location.state?.restaurantId ?? null;
  const initialRestaurantName = location.state?.restaurantName ?? null;

  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState(initialRestaurantId);
  const [menuData, setMenuData] = useState(null);
  const [selectedMenuId, setSelectedMenuId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [excludedAllergens, setExcludedAllergens] = useState([]);
  const [view, setView] = useState('menu'); // 'menu' | 'specials' | 'upsells' | 'quiz'

  // Load restaurants list on mount (for selector)
  useEffect(() => {
    if (!isInitialized) return;
    if (!userId) { navigate('/unified'); return; }
    let cancelled = false;
    (async () => {
      try {
        const list = await dataService.listRestaurants();
        if (!cancelled) setRestaurants(Array.isArray(list) ? list : (list?.restaurants || []));
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [userId, isInitialized, navigate]);

  // Load selected restaurant's menu data
  useEffect(() => {
    if (!selectedRestaurantId && selectedRestaurantId !== 0) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await dataService.getRestaurant(selectedRestaurantId);
        if (cancelled) return;
        if (data?.menus && data.menus.length > 0) {
          const menu = data.menus[0].menu_data;
          setMenuData(menu);
          if (menu?.menus?.length) setSelectedMenuId(menu.menus[0].id);
        } else {
          setMenuData(null);
        }
      } catch (e) {
        if (!cancelled) setError('Failed to load menu.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedRestaurantId]);

  const activeMenu = menuData?.menus?.find((m) => m.id === selectedMenuId) || menuData?.menus?.[0];
  const selectedRestaurant = restaurants.find((r) => String(r.id) === String(selectedRestaurantId));

  const items = useMemo(() => {
    if (!activeMenu?.categories) return [];
    const out = [];
    activeMenu.categories.forEach((cat) => {
      (cat.items || []).forEach((item) => {
        if (item.available === false) return;
        const tagged = (item.allergens || []).map((a) => cap(a));
        const text = `${item.name || ''} ${item.description || ''}`.toLowerCase();
        const inferred = Object.keys(ALLERGEN_KEYWORDS).filter((a) => {
          if (tagged.some((t) => t.toLowerCase() === a.toLowerCase())) return false;
          return ALLERGEN_KEYWORDS[a].some((k) => text.includes(k));
        });
        out.push({
          id: item.id,
          name: item.name,
          description: item.description || '',
          price: item.price,
          price_type: item.price_type || 'FIXED',
          dietary: (item.dietary_tags || []).map(cap),
          tagged, inferred,
          category: cat.name,
        });
      });
    });
    return out;
  }, [activeMenu]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (q && !`${it.name} ${it.description} ${it.category}`.toLowerCase().includes(q)) return false;
      if (excludedAllergens.length) {
        const hasAny = excludedAllergens.some((a) => it.tagged.includes(a) || it.inferred.includes(a));
        if (hasAny) return false;
      }
      return true;
    });
  }, [items, search, excludedAllergens]);

  const grouped = useMemo(() => {
    const groups = new Map();
    filtered.forEach((it) => {
      if (!groups.has(it.category)) groups.set(it.category, []);
      groups.get(it.category).push(it);
    });
    return Array.from(groups.entries());
  }, [filtered]);

  const specials = menuData?.specials || [];

  const toggleAllergen = (a) => setExcludedAllergens((prev) => prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]);

  if (!selectedRestaurantId && selectedRestaurantId !== 0) {
    return (
      <AppShell backdrop>
        <div style={{ maxWidth: 720, margin: '0 auto', padding: '60px 20px', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <div className="cwm-eyebrow-badge" style={{ marginBottom: 12 }}>
            <ShieldIcon size={12} /> SERVER DASHBOARD
          </div>
          <h1 style={{ fontSize: 34, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '-0.02em', margin: '0 0 10px' }}>
            Pick your <em style={{ fontStyle: 'italic', color: 'var(--cwm-blue-deep)' }}>workspace</em>.
          </h1>
          <p style={{ fontSize: 15, color: 'var(--cwm-gray-700)', marginBottom: 24, fontWeight: 500 }}>
            Choose a restaurant to start answering guest allergy questions fast.
          </p>
          <RestaurantPicker restaurants={restaurants} onPick={setSelectedRestaurantId} />
        </div>
      </AppShell>
    );
  }

  const inGuestMode = excludedAllergens.length > 0;

  return (
    <AppShell backdrop>
      <div style={{ maxWidth: 1040, margin: '0 auto', padding: '24px 20px 80px', position: 'relative', zIndex: 1 }}>

        {/* Hero — compact with inline restaurant switcher */}
        <motion.section
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.46, ease: [0.22, 1, 0.36, 1] }}
          style={{ marginBottom: 16, textAlign: 'center' }}
        >
          <div className="cwm-eyebrow-badge" style={{ marginBottom: 10 }}>
            <ShieldIcon size={12} /> SERVER DASHBOARD
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <h1 style={{ fontSize: 30, fontWeight: 900, letterSpacing: '-0.025em', color: 'var(--cwm-ink)', margin: 0 }}>
              {selectedRestaurant?.name || initialRestaurantName || 'Fast allergy lookup'}
            </h1>
            {!location.state?.restaurantId && (
              <button type="button" onClick={() => setSelectedRestaurantId(null)}
                      title="Switch restaurant"
                      style={{
                        width: 28, height: 28, borderRadius: 999,
                        background: 'var(--cwm-blue-pale)', color: 'var(--cwm-blue-deep)',
                        border: '1.5px solid var(--cwm-blue-light)',
                        display: 'grid', placeItems: 'center',
                        cursor: 'pointer', flexShrink: 0,
                      }}>
                <SwapIcon size={12} />
              </button>
            )}
          </div>
          <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--cwm-gray-500)', margin: 0 }}>
            {view === 'menu'     && 'Tap what a guest avoids — we hide everything that contains it.'}
            {view === 'specials' && "Tonight's features — memorize these and sell them first."}
            {view === 'upsells'  && 'Scripted pairings the house wants you to suggest.'}
            {view === 'quiz'     && 'Test your menu knowledge. 5 questions, 60 seconds.'}
          </p>
        </motion.section>

        {/* View switcher — Menu / Specials / Upsells / Quiz */}
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.04, ease: [0.22, 1, 0.36, 1] }}
          style={{
            display: 'flex', gap: 4, padding: 4,
            background: 'var(--cwm-gray-50)', borderRadius: 999,
            border: '1px solid var(--cwm-gray-200)',
            marginBottom: 14,
            overflowX: 'auto',
          }}
        >
          {SERVER_VIEWS.map((v) => {
            const on = view === v.id;
            const count =
              v.id === 'specials' ? (menuData?.specials?.length || 0) :
              v.id === 'upsells'  ? (menuData?.upsell_tips?.filter((t) => t.enabled !== false).length || 0) :
              null;
            return (
              // flex: 0 0 auto + parent overflowX:auto lets each pill size
              // to its label and horizontally scroll on mobile, instead of
              // squashing into each other below content width.
              <button key={v.id} type="button" onClick={() => setView(v.id)}
                      style={{
                        flex: '0 0 auto',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                        padding: '10px 12px', borderRadius: 999,
                        background: on ? '#fff' : 'transparent',
                        color: on ? 'var(--cwm-blue-deep)' : 'var(--cwm-gray-700)',
                        border: 'none', cursor: 'pointer',
                        fontFamily: 'var(--cwm-font)', fontSize: 12.5, fontWeight: 900, whiteSpace: 'nowrap',
                        boxShadow: on ? '0 4px 12px rgba(10,37,64,0.08)' : 'none',
                        transition: 'all 180ms ease',
                      }}>
                <ViewIcon name={v.icon} size={13} /> {v.label}
                {count != null && count > 0 && (
                  <span style={{
                    padding: '1px 7px', borderRadius: 999,
                    background: on ? 'var(--cwm-blue-pale)' : 'var(--cwm-gray-200)',
                    color: on ? 'var(--cwm-blue-deep)' : 'var(--cwm-gray-500)',
                    fontSize: 10.5, fontWeight: 900,
                  }}>{count}</span>
                )}
              </button>
            );
          })}
        </motion.div>

        {view === 'specials' && <SpecialsView specials={menuData?.specials || []} />}
        {view === 'upsells'  && <UpsellsView tips={menuData?.upsell_tips || []} items={items} />}
        {view === 'quiz'     && <QuizView items={items} />}
        {view === 'menu' && <>

        {/* Sticky filter card — search + allergen chips stay visible while scrolling the menu */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.46, delay: 0.06, ease: [0.22, 1, 0.36, 1] }}
          className="cwm-sheet"
          style={{
            position: 'sticky', top: 12, zIndex: 40,
            padding: 12, marginBottom: 14,
            boxShadow: '0 12px 28px rgba(13,71,161,0.12)',
            background: 'rgba(255,255,255,0.96)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
          }}
        >
          {/* Menu switcher (only when multiple menus) */}
          {menuData?.menus?.length > 1 && (
            <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 10, marginBottom: 10, borderBottom: '1px solid var(--cwm-gray-100)' }}>
              {menuData.menus.map((m) => {
                const on = m.id === selectedMenuId;
                return (
                  <button key={m.id} type="button" onClick={() => setSelectedMenuId(m.id)}
                          style={{
                            padding: '6px 14px', borderRadius: 999,
                            background: on ? 'linear-gradient(90deg, #1E9BFF, #0F7FE8)' : '#fff',
                            color: on ? '#fff' : 'var(--cwm-blue-deep)',
                            border: `1.5px solid ${on ? 'var(--cwm-blue-deep)' : 'var(--cwm-blue-light)'}`,
                            fontFamily: 'var(--cwm-font)', fontSize: 12.5, fontWeight: 900,
                            whiteSpace: 'nowrap', cursor: 'pointer',
                            boxShadow: on ? '0 3px 10px rgba(15,127,232,0.28)' : 'none',
                          }}>
                    {m.name}
                  </button>
                );
              })}
            </div>
          )}

          {/* Search */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <div style={{ padding: '0 6px 0 8px', color: 'var(--cwm-gray-500)' }}>
              <SearchIcon size={18} />
            </div>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search menu items or description…"
              style={{
                flex: 1, border: 'none', outline: 'none',
                padding: '8px 6px', background: 'transparent',
                fontFamily: 'var(--cwm-font)', fontSize: 15, fontWeight: 600,
                color: 'var(--cwm-ink)', minWidth: 0,
              }}
            />
            {search && (
              <button onClick={() => setSearch('')} aria-label="clear"
                      style={{ background: 'transparent', border: 'none', color: 'var(--cwm-gray-500)', cursor: 'pointer', padding: '6px 10px', fontSize: 16, fontWeight: 900 }}>
                ×
              </button>
            )}
          </div>

          {/* Allergen filters */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div className="cwm-eyebrow">
                GUEST AVOIDS
              </div>
              {inGuestMode && (
                <button type="button" onClick={() => setExcludedAllergens([])}
                        style={{
                          padding: '4px 10px', borderRadius: 999,
                          background: 'transparent', color: 'var(--cwm-gray-700)',
                          border: 'none',
                          fontFamily: 'var(--cwm-font)', fontSize: 11, fontWeight: 800,
                          cursor: 'pointer', textDecoration: 'underline',
                        }}>
                  Clear
                </button>
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {COMMON_ALLERGENS.map((a) => {
                const tone = ALLERGEN_TONES[a] || ALLERGEN_TONES.Gluten;
                const on = excludedAllergens.includes(a);
                return (
                  <motion.button
                    key={a} type="button" onClick={() => toggleAllergen(a)}
                    whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '5px 13px', borderRadius: 999,
                      background: on ? tone.dot : tone.bg,
                      color: on ? '#fff' : tone.fg,
                      border: `1.5px solid ${on ? tone.dot : 'transparent'}`,
                      fontFamily: 'var(--cwm-font)', fontSize: 12, fontWeight: 900, letterSpacing: '0.02em',
                      cursor: 'pointer',
                      boxShadow: on ? `0 4px 10px ${tone.dot}55` : 'none',
                    }}>
                    {on && <CheckIcon size={10} />} {a}
                  </motion.button>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* Guest-mode banner — confirms what server is answering when filters are active */}
        <AnimatePresence>
          {inGuestMode && (
            <motion.div
              initial={{ opacity: 0, y: -6, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, y: -6, height: 0 }}
              style={{
                overflow: 'hidden',
                marginBottom: 14,
              }}
            >
              <div style={{
                padding: '14px 18px',
                borderRadius: 18,
                background: 'linear-gradient(160deg, #E6FBEF 0%, #C5F0D4 100%)',
                border: '1.5px solid #A6E8C2',
                display: 'flex', alignItems: 'center', gap: 14,
              }}>
                <div style={{
                  width: 42, height: 42, borderRadius: 12,
                  background: '#fff', color: 'var(--cwm-green-text)',
                  display: 'grid', placeItems: 'center', flexShrink: 0,
                  boxShadow: '0 4px 10px rgba(22,128,60,0.18)',
                }}>
                  <ShieldIcon size={20} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="cwm-eyebrow" style={{ color: 'var(--cwm-green-text)' }}>
                    GUEST-SAFE MODE
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--cwm-ink)', marginTop: 2, lineHeight: 1.4 }}>
                    {filtered.length > 0
                      ? <>Showing <strong style={{ color: 'var(--cwm-green-text)' }}>{filtered.length}</strong> item{filtered.length === 1 ? '' : 's'} safe for guests avoiding <strong>{excludedAllergens.join(', ')}</strong>.</>
                      : <>No items safe for guests avoiding <strong>{excludedAllergens.join(', ')}</strong>. Flag a manager.</>}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Category quick-jump */}
        {grouped.length > 1 && (
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', marginBottom: 14, paddingBottom: 4 }}>
            {grouped.map(([categoryName, catItems]) => (
              <button key={categoryName} type="button"
                      onClick={() => {
                        const el = document.getElementById(`cwm-cat-${slugify(categoryName)}`);
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '6px 14px', borderRadius: 999,
                        background: '#fff', color: 'var(--cwm-blue-deep)',
                        border: '1.5px solid var(--cwm-blue-light)',
                        fontFamily: 'var(--cwm-font)', fontSize: 12, fontWeight: 800,
                        whiteSpace: 'nowrap', cursor: 'pointer', flexShrink: 0,
                      }}>
                {categoryName}
                <span style={{ fontSize: 10, color: 'var(--cwm-gray-500)', fontWeight: 800 }}>{catItems.length}</span>
              </button>
            ))}
          </div>
        )}

        {/* Specials rail */}
        {specials.length > 0 && (
          <div style={{ marginBottom: 18 }}>
            <div className="cwm-eyebrow" style={{ marginBottom: 8 }}>TONIGHT'S SPECIALS</div>
            <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 6 }}>
              {specials.map((s, i) => (
                <div key={s.id || i} style={{
                  minWidth: 220, flex: '0 0 220px',
                  padding: 14, borderRadius: 18,
                  background: 'linear-gradient(160deg, #FFF4E0, #FFE0B8)',
                  border: '1px solid rgba(255,140,0,0.2)',
                  boxShadow: '0 8px 18px rgba(255,140,0,0.12)',
                }}>
                  <div style={{ fontSize: 10.5, fontWeight: 900, letterSpacing: '0.08em', color: '#B45309', marginBottom: 4, textTransform: 'uppercase' }}>SPECIAL</div>
                  <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--cwm-ink)', marginBottom: 4 }}>{s.name}</div>
                  {s.description && <div style={{ fontSize: 12, color: 'var(--cwm-gray-700)', fontWeight: 500, lineHeight: 1.4 }}>{s.description}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Results */}
        {loading ? (
          <LoadingHero />
        ) : error ? (
          <ErrorHero message={error} />
        ) : items.length === 0 ? (
          <EmptyMenu />
        ) : grouped.length === 0 ? (
          <div className="cwm-sheet" style={{ padding: 40, textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 900, color: 'var(--cwm-ink)', marginBottom: 6 }}>No items match</div>
            <div style={{ fontSize: 13, color: 'var(--cwm-gray-500)' }}>Try clearing some allergen filters or adjusting your search.</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--cwm-gray-500)', textAlign: 'right' }}>
              {filtered.length} of {items.length} items
            </div>
            {grouped.map(([categoryName, catItems]) => (
              <section key={categoryName} id={`cwm-cat-${slugify(categoryName)}`} style={{ scrollMarginTop: 200 }}>
                <h2 style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '0.02em', textTransform: 'uppercase', margin: '0 4px 10px' }}>
                  {categoryName}
                  <span style={{ color: 'var(--cwm-gray-500)', fontWeight: 700, marginLeft: 8, letterSpacing: 0, textTransform: 'none' }}>
                    · {catItems.length}
                  </span>
                </h2>
                <div style={{ display: 'grid', gap: 10 }}>
                  {catItems.map((item, i) => (
                    <ItemCard key={item.id} item={item} index={i} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
        </>}
      </div>
    </AppShell>
  );
}

// ---------- sub-components ----------

function ItemCard({ item, index }) {
  const allAllergens = [...item.tagged, ...item.inferred];
  return (
    <motion.article
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, delay: Math.min(index * 0.02, 0.15) }}
      className="cwm-sheet" style={{ padding: 16 }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ fontSize: 16, fontWeight: 900, color: 'var(--cwm-ink)', margin: 0, letterSpacing: '-0.005em' }}>
            {item.name}
          </h3>
          {item.description && (
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--cwm-gray-700)', fontWeight: 500, lineHeight: 1.5 }}>
              {item.description}
            </p>
          )}
        </div>
        <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)', whiteSpace: 'nowrap' }}>
          {formatPrice(item.price, item.price_type)}
        </div>
      </div>
      {(item.dietary.length > 0 || allAllergens.length > 0) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 10 }}>
          {item.dietary.map((d) => (
            <span key={d} className="cwm-tag" style={{ background: 'var(--cwm-green-light)', color: 'var(--cwm-green-text)', border: '1px solid #A6E8C2' }}>
              ✓ {d}
            </span>
          ))}
          {allAllergens.map((a) => {
            const tone = ALLERGEN_TONES[a] || ALLERGEN_TONES.Gluten;
            const inferred = item.inferred.includes(a);
            return (
              <span key={a} className="cwm-tag" title={inferred ? 'Inferred from description' : 'Tagged by restaurant'}
                    style={{ background: tone.bg, color: tone.fg, border: `1px solid ${tone.dot}33` }}>
                {inferred ? '⚠︎' : '⚠'} {a}{inferred ? '?' : ''}
              </span>
            );
          })}
        </div>
      )}
    </motion.article>
  );
}

function RestaurantPicker({ restaurants, onPick }) {
  const [q, setQ] = useState('');
  const list = useMemo(() => {
    if (!q) return restaurants.slice(0, 24);
    return restaurants.filter((r) => (r.name || '').toLowerCase().includes(q.toLowerCase())).slice(0, 24);
  }, [restaurants, q]);
  return (
    <div>
      <div className="cwm-sheet" style={{ padding: 6, marginBottom: 14, display: 'flex', gap: 8, alignItems: 'center', maxWidth: 520, margin: '0 auto 14px' }}>
        <div style={{ padding: '0 6px', color: 'var(--cwm-gray-500)' }}><SearchIcon size={18} /></div>
        <input
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search restaurants…"
          style={{ flex: 1, border: 'none', outline: 'none', padding: '10px 6px', fontFamily: 'var(--cwm-font)', fontSize: 15, fontWeight: 600, color: 'var(--cwm-ink)' }}
        />
      </div>
      <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
        {list.map((r) => (
          <button key={r.id} type="button" onClick={() => onPick(r.id)}
                  className="cwm-sheet"
                  style={{ padding: 14, textAlign: 'left', cursor: 'pointer', background: '#fff', border: '1px solid rgba(255,255,255,0.9)' }}>
            <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--cwm-ink)', marginBottom: 2 }}>{r.name}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--cwm-gray-500)' }}>{r.address || r.cuisine_type || ''}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function LoadingHero() {
  return (
    <div style={{ padding: 60, textAlign: 'center' }}>
      <div style={{ width: 40, height: 40, margin: '0 auto 12px', border: '4px solid var(--cwm-blue-light)', borderTopColor: 'var(--cwm-blue-deep)', borderRadius: '50%', animation: 'cwmSpin 900ms linear infinite' }}/>
      <style>{`@keyframes cwmSpin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--cwm-gray-500)' }}>Loading menu…</div>
    </div>
  );
}

function ErrorHero({ message }) {
  return (
    <div className="cwm-sheet" style={{ padding: 40, textAlign: 'center' }}>
      <div style={{ fontSize: 16, fontWeight: 900, color: 'var(--cwm-red-text)', marginBottom: 6 }}>{message}</div>
      <div style={{ fontSize: 13, color: 'var(--cwm-gray-500)' }}>Try switching to a different restaurant.</div>
    </div>
  );
}

function EmptyMenu() {
  return (
    <div className="cwm-sheet" style={{ padding: 40, textAlign: 'center' }}>
      <div style={{ fontSize: 16, fontWeight: 900, color: 'var(--cwm-ink)', marginBottom: 6 }}>No menu items yet</div>
      <div style={{ fontSize: 13, color: 'var(--cwm-gray-500)' }}>Ask your manager to publish a menu in Menu Manager.</div>
    </div>
  );
}

// ---------- utils + icons ----------
function cap(s) { if (!s) return s; const x = String(s); return x.charAt(0).toUpperCase() + x.slice(1).toLowerCase(); }
function slugify(s) { return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''); }
// Menu data prices are inconsistent across restaurants — some store dollars
// (16, 16.50), some store cents (1600). Heuristic: integers >= 100 without
// decimals are cents, everything else is dollars. "MP" or null → market
// price. Strings pass through (e.g. "$14 - $18").
function formatPrice(price, priceType) {
  if (priceType === 'MP' || price == null) return 'MP';
  if (typeof price === 'string') return price.startsWith('$') ? price : `$${price}`;
  if (typeof price === 'number') {
    const dollars = Number.isInteger(price) && price >= 100 ? price / 100 : price;
    return `$${dollars.toFixed(2)}`;
  }
  return '';
}

function ShieldIcon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z"/></svg>; }
function SwapIcon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M7 10h12l-3-3M17 14H5l3 3"/></svg>; }
function SearchIcon({ size = 16 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.5-4.5"/></svg>; }
function CheckIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7"/></svg>; }

function ViewIcon({ name, size = 14 }) {
  const paths = {
    menu:       'M4 6h16M4 12h16M4 18h16',
    flame:      'M12 3s4 4 4 8a4 4 0 01-8 0c0-2 1-3 2-4 0 1 0 2 1 2 0-2 1-4 1-6z',
    'arrow-up': 'M12 19V5M5 12l7-7 7 7',
    quiz:       'M8 10h.01M12 10h.01M16 10h.01M21 12a9 9 0 01-4.5 7.8L17 22l-4-2a9 9 0 119-8z',
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d={paths[name] || paths.menu}/></svg>;
}

// ============================================================
// SPECIALS VIEW
// ============================================================

function SpecialsView({ specials }) {
  if (!specials.length) {
    return <EmptyView icon="flame" title="No specials posted" body="When the house publishes tonight's specials, they show up here with descriptions, pairings, and suggested pitches." />;
  }
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div className="cwm-eyebrow" style={{ color: '#B45309' }}>TONIGHT'S FEATURES ({specials.length})</div>
      <div style={{ display: 'grid', gap: 12 }}>
        {specials.map((s, i) => <SpecialCard key={s.id || i} special={s} index={i} />)}
      </div>
    </div>
  );
}

function SpecialCard({ special, index }) {
  const days = Array.isArray(special.available_days) ? special.available_days : [];
  const priceFmt = formatPrice(special.price, special.price_type);
  return (
    <motion.article
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, delay: index * 0.05, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -2 }}
      style={{
        position: 'relative', overflow: 'hidden',
        padding: 20,
        borderRadius: 22,
        background: 'linear-gradient(160deg, #FFF4E0 0%, #FFE0B8 100%)',
        border: '1.5px solid rgba(180,83,9,0.2)',
        boxShadow: '0 10px 24px rgba(180,83,9,0.14)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 999, background: '#fff', color: '#B45309', fontSize: 10.5, fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>
            <FlameIcon size={10} /> Special
          </div>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '-0.005em' }}>
            {special.name}
          </h3>
        </div>
        {priceFmt && (
          <div style={{ fontSize: 18, fontWeight: 900, color: 'var(--cwm-ink)', whiteSpace: 'nowrap', flexShrink: 0 }}>
            {priceFmt}
          </div>
        )}
      </div>
      {special.description && (
        <p style={{ margin: '0 0 10px', fontSize: 13.5, lineHeight: 1.5, color: 'var(--cwm-gray-700)', fontWeight: 500 }}>
          {special.description}
        </p>
      )}
      {days.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => {
            const on = days.includes(d);
            return (
              <span key={d} style={{
                padding: '3px 8px', borderRadius: 6,
                background: on ? '#B45309' : 'rgba(180,83,9,0.12)',
                color: on ? '#fff' : '#8A4A00',
                fontSize: 10.5, fontWeight: 900, letterSpacing: '0.04em',
              }}>{d}</span>
            );
          })}
        </div>
      )}
    </motion.article>
  );
}

// ============================================================
// UPSELLS VIEW
// ============================================================

function UpsellsView({ tips, items }) {
  const enabled = (tips || []).filter((t) => t.enabled !== false).sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  if (!enabled.length) {
    // Fall back to auto-generated suggestions from menu data so the view is
    // useful even when the restaurant hasn't published any tips yet.
    const auto = generateUpsellTips(items);
    if (!auto.length) {
      return <EmptyView icon="arrow-up" title="No upsell tips yet" body="When the house adds scripted pairings, they'll show up here with the exact line to say to guests." />;
    }
    return (
      <div>
        <div className="cwm-eyebrow" style={{ color: '#5B21B6', marginBottom: 10 }}>AUTO-SUGGESTED PAIRINGS ({auto.length})</div>
        <div style={{ fontSize: 12.5, color: 'var(--cwm-gray-500)', fontWeight: 600, marginBottom: 14 }}>
          Your restaurant hasn't added official tips yet. Here are suggested pairings based on menu categories.
        </div>
        <div style={{ display: 'grid', gap: 10 }}>
          {auto.map((t, i) => <UpsellCard key={i} tip={t} index={i} auto />)}
        </div>
      </div>
    );
  }
  return (
    <div>
      <div className="cwm-eyebrow" style={{ color: '#5B21B6', marginBottom: 10 }}>HOUSE PAIRINGS ({enabled.length})</div>
      <div style={{ display: 'grid', gap: 10 }}>
        {enabled.map((t, i) => <UpsellCard key={t.id || i} tip={t} index={i} />)}
      </div>
    </div>
  );
}

function UpsellCard({ tip, index, auto }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.04, ease: [0.22, 1, 0.36, 1] }}
      className="cwm-sheet"
      style={{ padding: 16, display: 'flex', gap: 14 }}
    >
      <div style={{
        width: 40, height: 40, borderRadius: 12,
        background: 'linear-gradient(160deg, #F1E8FE, #CFBAF0)', color: '#5B21B6',
        display: 'grid', placeItems: 'center', flexShrink: 0,
        border: '1.5px solid #CFBAF0',
      }}>
        <ArrowUpIcon size={18} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {(tip.trigger_item || tip.trigger) && (
          <div className="cwm-eyebrow" style={{ color: '#5B21B6', marginBottom: 4 }}>
            WHEN THEY ORDER {(tip.trigger_item || tip.trigger).toUpperCase()}
          </div>
        )}
        <div style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--cwm-ink)', fontWeight: 600 }}>
          {tip.text || tip.suggestion || ''}
        </div>
        {auto && (
          <div style={{ marginTop: 6, fontSize: 10.5, fontWeight: 800, color: 'var(--cwm-gray-400)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            AUTO-GENERATED
          </div>
        )}
      </div>
    </motion.article>
  );
}

// Naive pairing heuristic used when no house tips exist.
function generateUpsellTips(items) {
  if (!items?.length) return [];
  const byCat = items.reduce((acc, it) => {
    (acc[it.category] = acc[it.category] || []).push(it);
    return acc;
  }, {});
  const starters = byCat['Starters & Shareables'] || byCat['Starters'] || byCat['Appetizers'] || [];
  const drinks   = byCat['Drinks'] || byCat['Beverages'] || byCat['Cocktails'] || [];
  const desserts = byCat['Desserts'] || byCat['Sweets'] || [];
  const mains    = byCat['Burgers & Sandwiches'] || byCat['Entrees'] || byCat['Mains'] || [];

  const tips = [];
  if (mains.length && starters.length) {
    tips.push({ trigger_item: mains[0].name, text: `Suggest ${starters[0].name} to share while they wait — classic pairing, adds 20% to check average.` });
  }
  if (mains.length && drinks.length) {
    tips.push({ trigger_item: mains[0].name, text: `Pair with ${drinks[0].name} — complements the richness and encourages a second round.` });
  }
  if (mains.length && desserts.length) {
    tips.push({ trigger_item: mains[0].name, text: `Mention ${desserts[0].name} after entrees land — works best when they\u2019re still chatty.` });
  }
  if (starters.length >= 2) {
    tips.push({ trigger_item: starters[0].name, text: `"${starters[1].name} is our best pairing — most guests order both."` });
  }
  return tips;
}

// ============================================================
// QUIZ VIEW — gamified menu knowledge
// ============================================================

function QuizView({ items }) {
  const [game, setGame] = useState(null); // { questions, current, answers, startedAt }
  const [done, setDone]   = useState(false);

  const start = () => {
    if (!items?.length) return;
    const qs = buildQuizQuestions(items, 5);
    if (!qs.length) return;
    setGame({ questions: qs, current: 0, answers: [], startedAt: Date.now() });
    setDone(false);
  };

  const answer = (choiceIdx) => {
    if (!game) return;
    const q = game.questions[game.current];
    const nextAnswers = [...game.answers, { questionIdx: game.current, choice: choiceIdx, correct: choiceIdx === q.correctIdx }];
    if (game.current + 1 >= game.questions.length) {
      setGame({ ...game, answers: nextAnswers });
      setDone(true);
    } else {
      setGame({ ...game, answers: nextAnswers, current: game.current + 1 });
    }
  };

  const reset = () => { setGame(null); setDone(false); };

  if (!items?.length) {
    return <EmptyView icon="quiz" title="No menu to quiz yet" body="Load a menu to play the knowledge challenge." />;
  }

  if (!game) {
    return <QuizIntro onStart={start} itemCount={items.length} />;
  }

  if (done) {
    return <QuizResult game={game} onRestart={start} onExit={reset} />;
  }

  return <QuizRunner game={game} onAnswer={answer} onExit={reset} />;
}

function QuizIntro({ onStart, itemCount }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.46, ease: [0.22, 1, 0.36, 1] }}
      style={{
        padding: '32px 28px', borderRadius: 26,
        background: 'linear-gradient(160deg, #1E9BFF 0%, #0F7FE8 60%, #023E8A 100%)',
        color: '#fff',
        boxShadow: '0 20px 40px rgba(15,127,232,0.28)',
        position: 'relative', overflow: 'hidden',
      }}
    >
      <div aria-hidden style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: 'radial-gradient(circle, rgba(255,255,255,0.22) 0, rgba(255,255,255,0.22) 1.5px, transparent 1.6px)',
        backgroundSize: '22px 22px', opacity: 0.3,
      }}/>
      <div style={{ position: 'relative', textAlign: 'center' }}>
        <div style={{
          width: 64, height: 64, margin: '0 auto 14px',
          borderRadius: 20,
          background: 'rgba(255,255,255,0.22)',
          display: 'grid', placeItems: 'center',
          backdropFilter: 'blur(6px)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3)',
        }}>
          <QuizBigIcon size={32} />
        </div>
        <div style={{ fontSize: 10.5, fontWeight: 900, letterSpacing: '0.14em', textTransform: 'uppercase', opacity: 0.82, marginBottom: 6 }}>
          MENU KNOWLEDGE
        </div>
        <h2 style={{ fontSize: 28, fontWeight: 900, letterSpacing: '-0.025em', margin: '0 0 8px', lineHeight: 1.1 }}>
          Can you answer 5 guest questions?
        </h2>
        <p style={{ maxWidth: 380, margin: '0 auto 22px', fontSize: 14, fontWeight: 500, opacity: 0.92, lineHeight: 1.5 }}>
          Mix of allergen, pricing, category, and pairing questions drawn from the live {itemCount}-item menu. Beat 80% to earn the gold badge.
        </p>
        <button onClick={onStart}
                style={{
                  padding: '14px 32px', borderRadius: 999,
                  background: '#fff', color: 'var(--cwm-blue-deep)',
                  border: 'none', cursor: 'pointer',
                  fontFamily: 'var(--cwm-font)', fontSize: 15, fontWeight: 900,
                  boxShadow: '0 10px 22px rgba(10,37,64,0.2)',
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                }}>
          <PlayIcon size={14} /> Start quiz
        </button>
      </div>
    </motion.div>
  );
}

function QuizRunner({ game, onAnswer, onExit }) {
  const q = game.questions[game.current];
  const progress = ((game.current) / game.questions.length) * 100;
  const correctSoFar = game.answers.filter((a) => a.correct).length;
  return (
    <div>
      {/* Progress bar + header */}
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div className="cwm-eyebrow">QUESTION {game.current + 1} OF {game.questions.length}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 12.5, fontWeight: 900, color: 'var(--cwm-green-text)' }}>
            {correctSoFar} correct
          </span>
          <button onClick={onExit} style={{ background: 'transparent', border: 'none', color: 'var(--cwm-gray-500)', cursor: 'pointer', fontSize: 12, fontWeight: 800, textDecoration: 'underline' }}>
            Exit
          </button>
        </div>
      </div>

      <div style={{ height: 6, borderRadius: 999, background: 'var(--cwm-gray-100)', overflow: 'hidden', marginBottom: 20 }}>
        <motion.div
          initial={false}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          style={{ height: '100%', background: 'linear-gradient(90deg, #1E9BFF, #0F7FE8)' }}
        />
      </div>

      <motion.div
        key={game.current}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className="cwm-sheet"
        style={{ padding: 24 }}
      >
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 999, background: q.tone.bg, color: q.tone.fg, fontSize: 10.5, fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 12 }}>
          {q.category}
        </div>
        <h3 style={{ margin: '0 0 18px', fontSize: 18, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '-0.005em', lineHeight: 1.35 }}>
          {q.text}
        </h3>
        <div style={{ display: 'grid', gap: 8 }}>
          {q.choices.map((c, i) => (
            <motion.button
              key={i} type="button" onClick={() => onAnswer(i)}
              whileHover={{ x: 3 }} whileTap={{ scale: 0.98 }}
              style={{
                padding: '14px 18px', borderRadius: 14,
                background: '#fff',
                color: 'var(--cwm-ink)',
                border: '1.5px solid var(--cwm-blue-light)',
                fontFamily: 'var(--cwm-font)', fontSize: 14, fontWeight: 700,
                textAlign: 'left', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 12,
                transition: 'all 140ms ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--cwm-blue-pale)'; e.currentTarget.style.borderColor = 'var(--cwm-blue-deep)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = 'var(--cwm-blue-light)'; }}
            >
              <span style={{
                width: 26, height: 26, borderRadius: 8,
                background: 'var(--cwm-blue-pale)', color: 'var(--cwm-blue-deep)',
                display: 'grid', placeItems: 'center',
                fontSize: 12, fontWeight: 900, flexShrink: 0,
              }}>
                {String.fromCharCode(65 + i)}
              </span>
              {c}
            </motion.button>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

function QuizResult({ game, onRestart, onExit }) {
  const correct = game.answers.filter((a) => a.correct).length;
  const total = game.questions.length;
  const pct = Math.round((correct / total) * 100);
  const elapsed = Math.round((Date.now() - game.startedAt) / 1000);
  const badge =
    pct === 100 ? { label: 'Perfect', tone: 'green',  emoji: '⭐' } :
    pct >= 80   ? { label: 'Gold',    tone: 'amber',  emoji: '🥇' } :
    pct >= 60   ? { label: 'Silver',  tone: 'blue',   emoji: '🥈' } :
                  { label: 'Keep practicing', tone: 'red', emoji: '📚' };
  const toneColors = {
    green: { bg: '#E6FBEF', fg: '#0F7A3A' },
    amber: { bg: '#FFF3D6', fg: '#8A4A00' },
    blue:  { bg: '#E5F3FF', fg: '#0F5FB8' },
    red:   { bg: '#FFE9E9', fg: '#9F1818' },
  }[badge.tone];

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="cwm-sheet" style={{ padding: 28, textAlign: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 56, marginBottom: 8 }}>{badge.emoji}</div>
        <div style={{ display: 'inline-flex', padding: '4px 12px', borderRadius: 999, background: toneColors.bg, color: toneColors.fg, fontSize: 10.5, fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 12 }}>
          {badge.label}
        </div>
        <h2 style={{ fontSize: 44, fontWeight: 900, margin: '0 0 4px', color: 'var(--cwm-ink)', letterSpacing: '-0.03em' }}>
          {pct}%
        </h2>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--cwm-gray-500)', marginBottom: 18 }}>
          {correct} of {total} correct · {elapsed}s
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={onRestart} className="cwm-btn"><PlayIcon size={14} /> Try again</button>
          <button onClick={onExit} className="cwm-btn-secondary">Back to menu</button>
        </div>
      </div>
      {/* Review */}
      <div>
        <div className="cwm-eyebrow" style={{ marginBottom: 10 }}>REVIEW ANSWERS</div>
        <div style={{ display: 'grid', gap: 8 }}>
          {game.questions.map((q, i) => {
            const a = game.answers[i];
            const isCorrect = a?.correct;
            return (
              <div key={i} className="cwm-sheet" style={{ padding: 14, borderLeft: `4px solid ${isCorrect ? 'var(--cwm-green)' : 'var(--cwm-skip)'}` }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--cwm-ink)', marginBottom: 4 }}>
                  {q.text}
                </div>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: isCorrect ? 'var(--cwm-green-text)' : 'var(--cwm-red-text)' }}>
                  {isCorrect ? '✓ ' : '✗ '}
                  {isCorrect
                    ? q.choices[a.choice]
                    : <>You picked <strong>{q.choices[a?.choice] || '(none)'}</strong> · Correct: <strong>{q.choices[q.correctIdx]}</strong></>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}

// ============================================================
// QUIZ QUESTION BUILDER
// ============================================================

function buildQuizQuestions(items, count) {
  const pool = [];

  // Type A: allergen-contains (pick which dish contains X)
  const withAllergens = items.filter((i) => (i.tagged?.length || 0) > 0);
  withAllergens.forEach((item) => {
    const allergen = item.tagged[0];
    const distractors = items.filter((i) => !(i.tagged || []).includes(allergen) && !(i.inferred || []).includes(allergen)).slice(0, 3);
    if (distractors.length >= 3) {
      const choices = shuffle([item, ...distractors.slice(0, 3)]);
      pool.push({
        category: 'Allergens',
        tone: { bg: 'var(--cwm-red-light)', fg: 'var(--cwm-red-text)' },
        text: `Which dish contains ${allergen.toLowerCase()}?`,
        choices: choices.map((c) => c.name),
        correctIdx: choices.findIndex((c) => c.id === item.id),
      });
    }
  });

  // Type B: price lookup
  items.filter((i) => typeof i.price === 'number').forEach((item) => {
    const correct = typeof item.price === 'number' && Number.isInteger(item.price) && item.price >= 100
      ? item.price / 100 : item.price;
    const dollars = Math.round(correct);
    const choices = shuffle([
      `$${correct.toFixed(2)}`,
      `$${Math.max(2, dollars - 3).toFixed(2)}`,
      `$${(dollars + 2).toFixed(2)}`,
      `$${(dollars + 5).toFixed(2)}`,
    ]);
    pool.push({
      category: 'Pricing',
      tone: { bg: 'var(--cwm-blue-pale)', fg: 'var(--cwm-blue-deep)' },
      text: `What's the price of ${item.name}?`,
      choices,
      correctIdx: choices.findIndex((c) => c === `$${correct.toFixed(2)}`),
    });
  });

  // Type C: category lookup
  const categorySet = [...new Set(items.map((i) => i.category))];
  if (categorySet.length >= 4) {
    items.forEach((item) => {
      const wrong = categorySet.filter((c) => c !== item.category).slice(0, 3);
      if (wrong.length >= 3) {
        const choices = shuffle([item.category, ...wrong]);
        pool.push({
          category: 'Categories',
          tone: { bg: 'var(--cwm-green-light)', fg: 'var(--cwm-green-text)' },
          text: `Which category does ${item.name} belong to?`,
          choices,
          correctIdx: choices.findIndex((c) => c === item.category),
        });
      }
    });
  }

  // Type D: vegetarian / dietary-safe
  const vegetarian = items.filter((i) => (i.dietary || []).some((t) => /vegetarian|vegan/i.test(t)));
  if (vegetarian.length) {
    vegetarian.forEach((item) => {
      const others = items.filter((i) => i.id !== item.id && !(i.dietary || []).some((t) => /vegetarian|vegan/i.test(t))).slice(0, 3);
      if (others.length >= 3) {
        const choices = shuffle([item, ...others]);
        pool.push({
          category: 'Dietary',
          tone: { bg: '#F1E8FE', fg: '#5B21B6' },
          text: 'Which of these is vegetarian or vegan?',
          choices: choices.map((c) => c.name),
          correctIdx: choices.findIndex((c) => c.id === item.id),
        });
      }
    });
  }

  // Shuffle and sample, biased toward variety across categories
  const byCat = pool.reduce((acc, q) => { (acc[q.category] = acc[q.category] || []).push(q); return acc; }, {});
  const cats = Object.keys(byCat);
  const picked = [];
  for (let i = 0; i < count && cats.length > 0; i++) {
    const cat = cats[i % cats.length];
    const q = byCat[cat].shift();
    if (q) picked.push(q);
    if (!byCat[cat].length) cats.splice(cats.indexOf(cat), 1);
  }
  return shuffle(picked);
}

function shuffle(arr) {
  const copy = arr.slice();
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

// ============================================================
// SHARED SUB-COMPONENTS
// ============================================================

function EmptyView({ icon, title, body }) {
  return (
    <div style={{
      padding: '48px 24px', textAlign: 'center',
      borderRadius: 22, background: 'var(--cwm-gray-50)',
      border: '1.5px dashed var(--cwm-gray-300)',
    }}>
      <div style={{
        width: 60, height: 60, margin: '0 auto 14px', borderRadius: '50%',
        background: '#fff', color: 'var(--cwm-blue-deep)',
        display: 'grid', placeItems: 'center',
        border: '1.5px dashed var(--cwm-blue-light)',
      }}>
        <ViewIcon name={icon} size={24} />
      </div>
      <h3 style={{ fontSize: 18, fontWeight: 900, color: 'var(--cwm-ink)', margin: '0 0 6px' }}>{title}</h3>
      <p style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--cwm-gray-700)', maxWidth: 400, margin: '0 auto' }}>{body}</p>
    </div>
  );
}

function FlameIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 3s4 4 4 8a4 4 0 01-8 0c0-2 1-3 2-4 0 1 0 2 1 2 0-2 1-4 1-6z"/></svg>; }
function ArrowUpIcon({ size = 18 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5M5 12l7-7 7 7"/></svg>; }
function PlayIcon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M7 5l12 7-12 7z"/></svg>; }
function QuizBigIcon({ size = 32 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M9.1 9a3 3 0 015.8 1c0 2-3 3-3 3M12 17h.01"/></svg>; }
